import { useTheme } from "@amx/ux-twoui-theming";
import { Stack as FluentStack } from "@fluentui/react";
import React from "react";
import { IconSize } from "../../styles/icons";
import { Icon } from "../Icon/Icon";
import { TooltipHost } from "../TooltipHost/TooltipHost";
import { ImageProps } from "./Image.types";
import { getFallbackIconWrapperStyles } from "./ImageFallback.styles";

export const ImageFallback: React.FC<Partial<ImageProps>> = ({
    height,
    width,
    fallbackIconProps,
    ...otherProps
}) => {
    const theme = useTheme();

    if (height && width) {
        const heightNumber = dimensionToNumber(height);
        const widthNumber = dimensionToNumber(width);
        const minHeightOrWidth = Math.min(heightNumber, widthNumber);

        // 3 breakpoints for different fallback image sizes
        const largeBreakpoint = 100;
        const medBreakpoint = 50;
        const minBackPlateDimension = 36;
        const useBackPlate = minHeightOrWidth >= minBackPlateDimension;

        let size: IconSize;

        if (minHeightOrWidth >= largeBreakpoint) {
            size = "48px";
        } else if (minHeightOrWidth < largeBreakpoint && minHeightOrWidth >= medBreakpoint) {
            size = "32px";
        } else {
            size = "16px";
        }

        const icon = fallbackIconProps ?
            <Icon {...fallbackIconProps} size={size} /> :
            <Icon iconName="Photo2Remove" color="neutralTertiaryAlt" size={size} />;

        const fallbackImgWithoutTooltip = (
            <FluentStack
                styles={getFallbackIconWrapperStyles(theme, heightNumber, widthNumber, useBackPlate)}
                horizontalAlign="center"
                verticalAlign="center"
                role={otherProps.imageType === "altTextImage" ? "img" : "presentation"}
            >
                {icon}
            </FluentStack>
        );

        if (otherProps.imageType === "altTextImage") {
            return (
                <TooltipHost tooltipText={otherProps.alt!} tooltipPosition="top-center">
                    {fallbackImgWithoutTooltip}
                </TooltipHost>
            );
        } else {
            return fallbackImgWithoutTooltip;
        }
    }

    return null;
};

//Helper method to convert a dimensional string, eg: height="150px" into a number (150)
const dimensionToNumber = (dimension: string | number): number => {
    const pxAppendStr = "px";
    if (typeof dimension === "number") {
        return dimension;
    }
    if (dimension.includes(pxAppendStr)) {
        // unary "+" operator casts the string to a number
        return +dimension.substr(0, dimension.length - pxAppendStr.length);
    }
    return 0;
};
